// MCPTokensCard — `/dashboard/settings` 的「MCP 存取金鑰」段。
//
// docs/specs/mcp-read-only.md §9.1 / §13 PR-C
//
// 操作：
//   - 列出 user 自己的 key（label / prefix / 狀態 / 建立 / 最後使用 / 到期）
//   - 建立新 key → Drawer + form → 成功後一次性顯示 raw token + copy button
//   - 撤銷 key（軟刪除 + 後端清 KV cache、立刻生效）
//   - 連線教學：Claude Desktop / Cursor 的 mcp config 片段
//
// 權限：mcp:write.manage_own_keys（migration 0049 已 grant 給所有 active
// tenant role；除非 user 整把角色被 revoke、否則都看得到此卡）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  KeyRound, Plus, RefreshCw, Trash2, Copy, CheckCircle2, AlertTriangle,
  AlertCircle, ShieldCheck, Terminal, Plug,
} from "lucide-react";
import Drawer from "./Drawer.js";
import Skeleton from "./Skeleton.js";
import { Field } from "./Field.js";
import { useConfirmDialog } from "./ConfirmDialog.js";
import { formatTaipeiDateTime } from "../../lib/time.js";
import {
  listMyApiKeys, createMyApiKey, revokeMyApiKey,
  friendlyMcpKeysError,
  type MyApiKey, type IssuedApiKey,
} from "../lib/mcpKeysApi.js";
import {
  listConsents, revokeConsent, friendlyOAuthError,
  type ConsentRow,
} from "../lib/oauthApi.js";

type Stage =
  | { status: "loading" }
  | { status: "ready"; keys: MyApiKey[] }
  | { status: "error"; message: string };

const MCP_ENDPOINT = "https://mcp.nexus.sentry.red/mcp/sse";

export default function MCPTokensCard(): React.ReactElement {
  const [stage, setStage] = useState<Stage>({ status: "loading" });
  const [createOpen, setCreateOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { confirm } = useConfirmDialog();

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    try {
      const keys = await listMyApiKeys();
      setStage({ status: "ready", keys });
    } catch (err) {
      setStage({ status: "error", message: friendlyMcpKeysError(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onRevoke = async (k: MyApiKey): Promise<void> => {
    const ok = await confirm(
      `撤銷「${k.label ?? k.key_prefix}」？任何已用此金鑰連入的外部 LLM client 會立即失效。此動作無法復原（但可隨時建立新的）。`,
      { title: "撤銷此金鑰？", okLabel: "撤銷", destructive: true },
    );
    if (!ok) return;
    setBusyId(k.id);
    try {
      await revokeMyApiKey(k.id);
      setToast(`已撤銷「${k.label ?? k.key_prefix}」。`);
      void load();
    } catch (err) {
      setToast(friendlyMcpKeysError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card p-4 sm:p-5 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-raised border border-border">
          <KeyRound className="h-4 w-4 text-accent" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-text-primary">MCP 存取金鑰</h2>
          <p className="mt-0.5 text-xs text-text-secondary leading-snug">
            金鑰用於不走 OAuth 流程的 MCP client（例 Claude Desktop 的
            <code className="font-mono text-2xs bg-bg-base px-1 rounded">claude_desktop_config.json</code>、
            Cursor 的 <code className="font-mono text-2xs bg-bg-base px-1 rounded">.cursor/mcp.json</code>、
            自寫 script）。權限 = 您在 dashboard 內的權限全集（含寫入 / 執行）。
            <br />
            走 OAuth 的應用（Claude.ai、ChatGPT）發出的 token <strong>不在此列</strong>，
            請看下方「<strong>已連線的外部 LLM 應用</strong>」段落管理。
            <br />
            <strong className="text-warning">金鑰明文僅在建立時顯示一次</strong>，無法事後找回。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
          <button
            type="button"
            onClick={() => void load()}
            className="btn-secondary text-xs"
            disabled={stage.status === "loading"}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${stage.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="btn-primary text-xs"
          >
            <Plus className="h-3.5 w-3.5" /> 建立金鑰
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-3 flex items-center gap-2 rounded border border-success/30 bg-success/10 p-2 text-xs text-success">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="flex-1">{toast}</span>
          <button className="text-2xs underline" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}

      {stage.status === "loading" && <Skeleton className="h-24 w-full" />}
      {stage.status === "error" && (
        <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{stage.message}</span>
        </div>
      )}
      {stage.status === "ready" && stage.keys.length === 0 && (
        <p className="text-xs text-text-muted py-4 text-center">
          目前還沒有任何金鑰。點右上「建立金鑰」開始接入 Claude Desktop / Cursor。
        </p>
      )}
      {stage.status === "ready" && stage.keys.length > 0 && (
        <TabbedKeyList keys={stage.keys} busyId={busyId} onRevoke={onRevoke} />
      )}

      <ConsentsSection />

      <ConnectionGuide />

      <MCPTokenCreateDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onIssued={() => { setToast("金鑰已建立 — 請立刻複製貼到 client config。"); void load(); }}
      />
    </div>
  );
}

// ─── OAuth consents (Claude.ai / ChatGPT / etc. 透過 OAuth 授權的 apps) ─

type ConsentStage =
  | { status: "loading" }
  | { status: "ready"; items: ConsentRow[] }
  | { status: "error"; message: string };

function ConsentsSection(): React.ReactElement {
  const [stage, setStage] = useState<ConsentStage>({ status: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { confirm } = useConfirmDialog();

  const load = useCallback(async () => {
    setStage({ status: "loading" });
    try {
      const items = await listConsents();
      setStage({ status: "ready", items });
    } catch (err) {
      setStage({ status: "error", message: friendlyOAuthError(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onRevoke = async (row: ConsentRow): Promise<void> => {
    const ok = await confirm(
      `撤銷對「${row.client_name}」的授權？該應用 (含所有已發出的 access / refresh token) 會立即失去存取 SENTRY 資料的能力。`,
      { title: "撤銷此 OAuth 連線？", okLabel: "撤銷", destructive: true },
    );
    if (!ok) return;
    setBusyId(row.client_id);
    try {
      const n = await revokeConsent(row.client_id);
      setToast(`已撤銷「${row.client_name}」+ ${n} 個 active token。`);
      void load();
    } catch (err) {
      setToast(friendlyOAuthError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-border">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-3">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-bg-raised border border-border">
          <Plug className="h-3.5 w-3.5 text-accent" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-text-primary">已連線的外部 LLM 應用</h3>
          <p className="mt-0.5 text-2xs text-text-secondary leading-snug">
            透過 OAuth 授權的應用程式（Claude.ai、ChatGPT 等）。撤銷會立即失效。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="btn-secondary text-2xs"
          disabled={stage.status === "loading"}
        >
          <RefreshCw className={`h-3 w-3 ${stage.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
        </button>
      </div>

      {toast && (
        <div className="mb-3 flex items-center gap-2 rounded border border-success/30 bg-success/10 p-2 text-xs text-success">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="flex-1">{toast}</span>
          <button className="text-2xs underline" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}

      {stage.status === "loading" && <Skeleton className="h-16 w-full" />}
      {stage.status === "error" && (
        <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{stage.message}</span>
        </div>
      )}
      {stage.status === "ready" && stage.items.length === 0 && (
        <p className="text-2xs text-text-muted py-3 text-center">
          目前沒有透過 OAuth 連線的應用。在 Claude.ai 等 client 加入「Custom Connector」並貼入
          <code className="mx-1 font-mono bg-bg-base px-1 rounded">https://mcp.nexus.sentry.red/mcp/sse</code>
          即可走 OAuth 流程。
        </p>
      )}
      {stage.status === "ready" && stage.items.length > 0 && (
        <ul className="space-y-2">
          {stage.items.map((c) => (
            <li key={c.client_id} className="rounded border border-border bg-bg-raised p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                  <span className="text-sm font-medium text-text-primary truncate">{c.client_name}</span>
                  <span className="text-2xs uppercase tracking-wider rounded px-1.5 py-0.5 border border-success/40 bg-success/10 text-success">
                    {c.active_token_count} active token
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-2xs text-text-muted">
                  <span className="font-mono">{c.client_id}</span>
                  {c.software_id && <span>software: {c.software_id}</span>}
                  <span>建立：{formatTaipeiDateTime(Date.parse(c.created_at))}</span>
                  <span>最後使用：{c.last_used_at ? formatTaipeiDateTime(Date.parse(c.last_used_at)) : "—"}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void onRevoke(c)}
                className="btn-secondary text-2xs text-danger flex-shrink-0 self-start sm:self-center"
                disabled={busyId === c.client_id}
                aria-label="撤銷此 OAuth 連線"
              >
                <Trash2 className="h-3 w-3" /> {busyId === c.client_id ? "撤銷中…" : "撤銷"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KeyList({
  keys, busyId, onRevoke,
}: {
  keys: MyApiKey[];
  busyId: string | null;
  onRevoke: (k: MyApiKey) => Promise<void>;
}): React.ReactElement {
  return (
    <ul className="space-y-2">
      {keys.map((k) => (
        <li key={k.id} className="rounded border border-border bg-bg-raised p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              <span className="text-sm font-medium text-text-primary truncate">
                {k.label ?? <span className="text-text-muted italic">未命名</span>}
              </span>
              <StatusBadge status={k.status} />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-2xs text-text-muted">
              <span className="font-mono">{k.key_prefix}…</span>
              <span>建立：{formatTaipeiDateTime(Date.parse(k.created_at))}</span>
              <span>最後使用：{k.last_used_at ? formatTaipeiDateTime(Date.parse(k.last_used_at)) : "—"}</span>
              <span>到期：{k.expires_at ? formatTaipeiDateTime(Date.parse(k.expires_at)) : "永不過期"}</span>
            </div>
          </div>
          {k.status === "active" && (
            <button
              type="button"
              onClick={() => void onRevoke(k)}
              className="btn-secondary text-2xs text-danger flex-shrink-0 self-start sm:self-center"
              disabled={busyId === k.id}
              aria-label="撤銷此金鑰"
            >
              <Trash2 className="h-3 w-3" /> {busyId === k.id ? "撤銷中…" : "撤銷"}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

// ─── Tabs + pagination wrapper around KeyList ──────────────────────
// 3 tab（已啟用 / 已撤銷 / 已過期）+ 每 tab 5 筆/頁。切 tab 時 page
// reset 回 1 — 跨 tab 共享 page 數字邏輯上沒意義。

type TabKey = "active" | "revoked" | "expired";
const PAGE_SIZE = 5;
const TAB_META: Record<TabKey, { label: string; matchStatus: MyApiKey["status"] }> = {
  active:  { label: "已啟用", matchStatus: "active" },
  revoked: { label: "已撤銷", matchStatus: "revoked" },
  expired: { label: "已過期", matchStatus: "expired" },
};
const TAB_ORDER: TabKey[] = ["active", "revoked", "expired"];

function TabbedKeyList({
  keys, busyId, onRevoke,
}: {
  keys: MyApiKey[];
  busyId: string | null;
  onRevoke: (k: MyApiKey) => Promise<void>;
}): React.ReactElement {
  const [tab, setTab] = useState<TabKey>("active");
  const [page, setPage] = useState(1);

  // Pre-compute per-tab buckets so the tab strip can show counts +
  // current-tab slice is cheap.
  const buckets = useMemo(() => {
    const out: Record<TabKey, MyApiKey[]> = { active: [], revoked: [], expired: [] };
    for (const k of keys) {
      const t = TAB_ORDER.find((tk) => TAB_META[tk].matchStatus === k.status);
      if (t) out[t].push(k);
    }
    return out;
  }, [keys]);

  // Auto-fall-back to a non-empty tab on first render so user doesn't
  // land on an empty 已啟用 with no obvious next step.
  useEffect(() => {
    if (buckets[tab].length > 0) return;
    const firstNonEmpty = TAB_ORDER.find((t) => buckets[t].length > 0);
    if (firstNonEmpty && firstNonEmpty !== tab) setTab(firstNonEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount + when keys change
  }, [keys]);

  const onTabChange = (t: TabKey): void => { setTab(t); setPage(1); };

  const currentBucket = buckets[tab];
  const total = currentBucket.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Clamp page when bucket shrinks (e.g. after revoke).
  const clampedPage = Math.min(page, totalPages);
  const pageStart = (clampedPage - 1) * PAGE_SIZE;
  const pageItems = currentBucket.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div>
      {/* Tab strip */}
      <div
        role="tablist"
        aria-label="金鑰狀態"
        className="flex flex-wrap gap-1 mb-3 border-b border-border"
      >
        {TAB_ORDER.map((t) => {
          const isActive = t === tab;
          const count = buckets[t].length;
          return (
            <button
              key={t}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => onTabChange(t)}
              className={
                "relative -mb-px rounded-t-md border border-b-0 px-3 py-1.5 text-xs transition-colors " +
                (isActive
                  ? "border-border bg-bg-surface text-text-primary"
                  : "border-transparent bg-transparent text-text-secondary hover:text-text-primary hover:bg-bg-raised")
              }
            >
              {TAB_META[t].label}
              <span className={
                "ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-[10px] tabular-nums " +
                (isActive ? "bg-accent/15 text-accent" : "bg-bg-raised text-text-muted")
              }>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {pageItems.length === 0 ? (
        <p className="text-xs text-text-muted py-6 text-center">
          {TAB_META[tab].label}的金鑰：0 筆。
        </p>
      ) : (
        <KeyList keys={pageItems} busyId={busyId} onRevoke={onRevoke} />
      )}

      {/* Pagination footer — only show when more than one page. */}
      {totalPages > 1 && (
        <PageFooter
          page={clampedPage}
          totalPages={totalPages}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          onJump={setPage}
        />
      )}
    </div>
  );
}

function PageFooter({
  page, totalPages, total, onPrev, onNext, onJump,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onJump: (p: number) => void;
}): React.ReactElement {
  // Compact pager — list page numbers up to 5 around current.
  const around = pageWindow(page, totalPages, 5);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
      <span className="text-2xs text-text-muted tabular-nums">
        第 {page} / {totalPages} 頁 · 共 {total} 筆
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 1}
          className="btn-secondary text-2xs px-2 py-1 disabled:opacity-40"
          aria-label="上一頁"
        >
          ‹
        </button>
        {around[0] > 1 && <span className="text-2xs text-text-muted px-1">…</span>}
        {around.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onJump(n)}
            aria-current={n === page ? "page" : undefined}
            className={
              "min-w-[26px] rounded border px-1.5 py-0.5 text-2xs tabular-nums " +
              (n === page
                ? "border-accent bg-accent/10 text-accent"
                : "border-border bg-bg-raised text-text-secondary hover:text-text-primary")
            }
          >
            {n}
          </button>
        ))}
        {around[around.length - 1] < totalPages && <span className="text-2xs text-text-muted px-1">…</span>}
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages}
          className="btn-secondary text-2xs px-2 py-1 disabled:opacity-40"
          aria-label="下一頁"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function pageWindow(page: number, totalPages: number, maxWindow: number): number[] {
  const half = Math.floor(maxWindow / 2);
  let start = Math.max(1, page - half);
  let end = Math.min(totalPages, start + maxWindow - 1);
  start = Math.max(1, end - maxWindow + 1);
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}

function StatusBadge({ status }: { status: MyApiKey["status"] }): React.ReactElement {
  const meta = {
    active:  { label: "啟用中", cls: "text-success border-success/40 bg-success/10" },
    expired: { label: "已過期", cls: "text-warning border-warning/40 bg-warning/10" },
    revoked: { label: "已撤銷", cls: "text-text-muted border-border bg-bg-base" },
  }[status];
  return (
    <span className={`text-2xs uppercase tracking-wider rounded px-1.5 py-0.5 border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// ─── Create drawer ─────────────────────────────────────────────────

const EXPIRY_OPTIONS = [
  { label: "30 天", days: 30 },
  { label: "90 天 (預設)", days: 90 },
  { label: "365 天", days: 365 },
  { label: "永不過期", days: null },
] as const;

function MCPTokenCreateDrawer({
  open, onClose, onIssued,
}: {
  open: boolean;
  onClose: () => void;
  onIssued: () => void;
}): React.ReactElement {
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);

  // Reset on close so a stale issued card doesn't show on next open.
  useEffect(() => { if (!open) setIssued(null); }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="建立 MCP 金鑰"
      description="此金鑰只能讀資料、不能寫入；權限上限就是您 dashboard 內已有的權限。"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">
            {issued ? "完成" : "取消"}
          </button>
        </div>
      }
    >
      {issued ? (
        <IssuedKeyCard issued={issued} onAnother={() => setIssued(null)} />
      ) : (
        <CreateKeyForm onIssued={(k) => { setIssued(k); onIssued(); }} onCancel={onClose} />
      )}
    </Drawer>
  );
}

function CreateKeyForm({
  onIssued, onCancel,
}: {
  onIssued: (k: IssuedApiKey) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState<number | null>(90);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const expiresAt = expiryDays === null
        ? null
        : new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString();
      const k = await createMyApiKey({
        label: label.trim() || null,
        expires_at: expiresAt,
      });
      onIssued(k);
    } catch (e) {
      setErr(friendlyMcpKeysError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="名稱（可選）" hint="自己看的標籤，例如「家裡的 Cursor」、「公司 MacBook 的 Claude Desktop」">
        <input
          className="input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={100}
          placeholder="例如：Cursor on laptop"
        />
      </Field>

      <Field label="到期時間" required>
        <div className="flex flex-wrap gap-2">
          {EXPIRY_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => setExpiryDays(opt.days)}
              className={
                "rounded-md border px-3 py-1.5 text-xs " +
                (expiryDays === opt.days
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-border bg-bg-raised text-text-secondary hover:bg-bg-base")
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-2xs text-text-muted">
          到期後使用此金鑰的 MCP 連線會收到 401，需重新建立金鑰。
        </p>
      </Field>

      <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-2xs text-warning leading-relaxed flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">金鑰明文僅顯示一次</p>
          <p className="mt-0.5">建立後請立刻複製貼到 client config。離開此頁後將無法再次取得；遺失只能撤銷再建一把。</p>
        </div>
      </div>

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary text-sm" disabled={busy}>
          取消
        </button>
        <button type="submit" className="btn-primary text-sm" disabled={busy}>
          <ShieldCheck className="h-4 w-4" /> {busy ? "建立中…" : "建立金鑰"}
        </button>
      </div>
    </form>
  );
}

function IssuedKeyCard({
  issued, onAnother,
}: {
  issued: IssuedApiKey;
  onAnother: () => void;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(issued.raw_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked → user copies manually */ }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-success/40 bg-success/5 p-4">
        <div className="flex items-center gap-2 text-success">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-semibold">金鑰已建立</span>
        </div>

        <div className="mt-3">
          <div className="text-2xs text-text-muted uppercase tracking-wider">金鑰明文（只顯示一次）</div>
          <div className="mt-1 flex items-stretch gap-2">
            <code className="font-mono text-xs break-all bg-bg-base px-3 py-2 rounded border border-border flex-1">
              {issued.raw_token}
            </code>
            <button type="button" onClick={() => void copy()} className="btn-secondary text-xs">
              <Copy className="h-3.5 w-3.5" /> {copied ? "已複製" : "複製"}
            </button>
          </div>
        </div>

        <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-2xs text-warning leading-relaxed flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            離開此頁後將無法再取回此金鑰。請現在就把上方字串複製到您的 Claude Desktop /
            Cursor / 其他 MCP client 的 config 內。
          </span>
        </div>

        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-2xs">
          <dt className="text-text-muted">名稱</dt><dd>{issued.label ?? <span className="text-text-muted italic">未命名</span>}</dd>
          <dt className="text-text-muted">前綴</dt><dd className="font-mono">{issued.key_prefix}…</dd>
          <dt className="text-text-muted">到期</dt>
          <dd>{issued.expires_at ? formatTaipeiDateTime(Date.parse(issued.expires_at)) : "永不過期"}</dd>
          <dt className="text-text-muted">範圍</dt><dd>{issued.scope}</dd>
        </dl>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={onAnother} className="btn-secondary text-sm">再建一把</button>
      </div>
    </div>
  );
}

// ─── Connection guide ──────────────────────────────────────────────

function ConnectionGuide(): React.ReactElement {
  return (
    <details className="mt-4 rounded-lg border border-border bg-bg-raised p-3">
      <summary className="cursor-pointer text-sm text-text-primary font-medium flex items-center gap-2">
        <Terminal className="h-4 w-4 text-accent" />
        連線教學（Claude Desktop / Cursor）
      </summary>
      <div className="mt-3 space-y-4 text-xs text-text-secondary leading-relaxed">
        <p>
          MCP endpoint：<code className="font-mono bg-bg-base px-1.5 py-0.5 rounded">{MCP_ENDPOINT}</code>。
          認證方式：<code className="font-mono bg-bg-base px-1.5 py-0.5 rounded">Authorization: Bearer sntry_mcp_*</code>。
          以下兩種 client 都透過官方 <code className="font-mono bg-bg-base px-1.5 py-0.5 rounded">mcp-remote</code> 橋接 stdio ↔ 我們的 Streamable HTTP。
        </p>

        <CodeSnippet
          title="Claude Desktop"
          subtitle="macOS：~/Library/Application Support/Claude/claude_desktop_config.json"
          code={`{
  "mcpServers": {
    "sentry-location": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "${MCP_ENDPOINT}",
        "--header",
        "Authorization:Bearer \${SENTRY_MCP_KEY}"
      ],
      "env": {
        "SENTRY_MCP_KEY": "sntry_mcp_............................"
      }
    }
  }
}`}
        />

        <CodeSnippet
          title="Cursor"
          subtitle="專案根目錄：.cursor/mcp.json（或全域 ~/.cursor/mcp.json）"
          code={`{
  "mcpServers": {
    "sentry-location": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "${MCP_ENDPOINT}",
        "--header",
        "Authorization:Bearer sntry_mcp_............................"
      ]
    }
  }
}`}
        />

        <p className="text-2xs text-text-muted">
          設定完成後重新啟動 client。Claude Desktop 在「Tools」面板會看到 26 個 SENTRY tool；
          Cursor 在 Composer 內鍵入 <code className="font-mono">@sentry-location</code> 即可選用。
        </p>
      </div>
    </details>
  );
}

function CodeSnippet({
  title, subtitle, code,
}: {
  title: string;
  subtitle: string;
  code: string;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="rounded-md border border-border bg-bg-base">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="min-w-0">
          <div className="text-2xs uppercase tracking-wider text-text-muted">{title}</div>
          <div className="font-mono text-2xs text-text-secondary truncate">{subtitle}</div>
        </div>
        <button type="button" onClick={() => void copy()} className="btn-ghost text-2xs flex-shrink-0">
          <Copy className="h-3 w-3" /> {copied ? "已複製" : "複製"}
        </button>
      </div>
      <pre className="p-3 text-2xs overflow-x-auto leading-relaxed text-text-secondary"><code>{code}</code></pre>
    </div>
  );
}
